import React, { useEffect, useState } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { Plus, Users, FileText, Edit3, Trash2 } from 'lucide-react';

const Owners = () => {
    const [owners, setOwners] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showForm, setShowForm] = useState(false);
    const [editingOwner, setEditingOwner] = useState(null);

    const load = async () => {
        setLoading(true);
        try { const r = await axios.get('/api/owners'); setOwners(r.data.owners || []); }
        catch { toast.error('Failed to load owners'); }
        finally { setLoading(false); }
    };
    useEffect(() => { load(); }, []);

    const handleDelete = async (id, name) => {
        if (!window.confirm(`Delete owner "${name}"? This cannot be undone.`)) return;
        try {
            await axios.delete(`/api/owners/${id}`);
            toast.success('Owner deleted');
            load();
        } catch (err) {
            toast.error(err.response?.data?.message || 'Failed to delete');
        }
    };

    return (
        <div className="max-w-6xl mx-auto space-y-6">
            <div className="flex justify-between items-center">
                <div>
                    <h1 className="text-2xl font-bold">Owners</h1>
                    <p className="text-gray-600">Property owners you manage for. They can log in to see their statements.</p>
                </div>
                <button onClick={() => { setEditingOwner(null); setShowForm(true); }} className="inline-flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700">
                    <Plus className="h-4 w-4" /> New owner
                </button>
            </div>

            {loading ? <div>Loading…</div> : owners.length === 0 ? (
                <div className="bg-white border border-dashed rounded-xl p-10 text-center">
                    <Users className="h-10 w-10 mx-auto text-primary-500" />
                    <p className="mt-2 text-gray-600">No owners registered yet.</p>
                </div>
            ) : (
                <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
                    <table className="w-full text-sm">
                        <thead className="bg-gray-50 text-left border-b">
                            <tr>
                                <th className="p-3">Name</th>
                                <th className="p-3">Email</th>
                                <th className="p-3">Phone</th>
                                <th className="p-3">Since</th>
                                <th className="p-3 text-right">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {owners.map(o => (
                                <tr key={o.id} className="border-t hover:bg-gray-50">
                                    <td className="p-3">{o.first_name} {o.last_name}</td>
                                    <td className="p-3">{o.email}</td>
                                    <td className="p-3">{o.phone || '—'}</td>
                                    <td className="p-3">{new Date(o.created_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}</td>
                                    <td className="p-3 text-right flex gap-2 justify-end">
                                        <button
                                            onClick={() => { setEditingOwner(o); setShowForm(true); }}
                                            className="p-1.5 text-blue-600 hover:bg-blue-50 rounded transition-colors"
                                            title="Edit owner"
                                        >
                                            <Edit3 className="h-4 w-4" />
                                        </button>
                                        <button
                                            onClick={() => handleDelete(o.id, `${o.first_name} ${o.last_name}`)}
                                            className="p-1.5 text-red-600 hover:bg-red-50 rounded transition-colors"
                                            title="Delete owner"
                                        >
                                            <Trash2 className="h-4 w-4" />
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {showForm && <OwnerForm owner={editingOwner} onClose={() => { setShowForm(false); setEditingOwner(null); }} onSaved={() => { setShowForm(false); setEditingOwner(null); load(); }} />}
        </div>
    );
};

const OwnerForm = ({ owner, onClose, onSaved }) => {
    const [f, setF] = useState(owner ? { first_name: owner.first_name, last_name: owner.last_name, email: owner.email, phone: owner.phone || '' } : { first_name: '', last_name: '', email: '', phone: '' });
    const [saving, setSaving] = useState(false);
    const [tempPw, setTempPw] = useState(null);

    const submit = async e => {
        e.preventDefault();
        setSaving(true);
        try {
            if (owner) {
                // Edit existing owner
                await axios.put(`/api/owners/${owner.id}`, f);
                toast.success('Owner updated');
                onSaved();
            } else {
                // Create new owner
                const { data } = await axios.post('/api/owners', f);
                setTempPw(data.temp_password);
            }
        } catch (err) {
            toast.error(err.response?.data?.message || 'Failed');
            setSaving(false);
        }
    };

    if (tempPw) {
        return (
            <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
                <div className="bg-white rounded-xl max-w-md p-6">
                    <h2 className="font-semibold mb-2">Owner created</h2>
                    <p className="text-sm text-gray-600 mb-3">Share this temporary password with them so they can log in and reset it.</p>
                    <div className="bg-gray-50 border rounded p-3 font-mono text-sm">{tempPw}</div>
                    <div className="text-right mt-4"><button className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700" onClick={() => { onSaved(); }}>Done</button></div>
                </div>
            </div>
        );
    }

    return (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
            <form onSubmit={submit} className="bg-white rounded-xl max-w-md w-full">
                <div className="p-4 border-b flex justify-between">
                    <h2 className="font-semibold">{owner ? 'Edit owner' : 'New owner'}</h2>
                    <button type="button" onClick={onClose} className="text-gray-500 hover:text-gray-700">✕</button>
                </div>
                <div className="p-4 grid grid-cols-2 gap-3">
                    <input
                        required
                        placeholder="First name"
                        className="border rounded-lg px-3 py-2"
                        value={f.first_name}
                        onChange={e => setF({ ...f, first_name: e.target.value })}
                    />
                    <input
                        required
                        placeholder="Last name"
                        className="border rounded-lg px-3 py-2"
                        value={f.last_name}
                        onChange={e => setF({ ...f, last_name: e.target.value })}
                    />
                    <input
                        required
                        type="email"
                        placeholder="Email"
                        className="border rounded-lg px-3 py-2 col-span-2"
                        value={f.email}
                        onChange={e => setF({ ...f, email: e.target.value })}
                    />
                    <input
                        placeholder="Phone"
                        className="border rounded-lg px-3 py-2 col-span-2"
                        value={f.phone}
                        onChange={e => setF({ ...f, phone: e.target.value })}
                    />
                </div>
                <div className="p-4 border-t flex justify-end gap-2">
                    <button type="button" onClick={onClose} className="px-4 py-2 border rounded-lg hover:bg-gray-50">Cancel</button>
                    <button disabled={saving} className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50">
                        {saving ? 'Saving…' : owner ? 'Update' : 'Create'}
                    </button>
                </div>
            </form>
        </div>
    );
};

export default Owners;
